import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays } from 'date-fns';

/**
 * Estimates a mediator's "next slot" lead time from their actual past
 * publishing rhythm. Looks at every availability_slot they had (booked or
 * free, regardless of current state) over the last 28 days and counts how
 * many distinct calendar dates they opened. We then turn that into a
 * human-readable wait-time estimate for the "Sans créneaux" popover.
 *
 * Only enabled when an `enabled` flag is true (we don't want to fetch this
 * for every card in a search result — only when the user actually opens
 * the popover for a specific mediator).
 */
export interface AvailabilityEstimate {
  /** Distinct dates the mediator published over the last 28 days. */
  publishedDays: number;
  /** Average days between two opening days, computed from publishedDays. */
  avgIntervalDays: number | null;
  /** A short human label like "sous environ 5 jours" or "sous 1 à 2 semaines". */
  label: string;
  /** True when we have no historical data at all to base an estimate on. */
  isFallback: boolean;
}

const WINDOW_DAYS = 28;

const buildLabel = (publishedDays: number): {
  label: string;
  avgIntervalDays: number | null;
  isFallback: boolean;
} => {
  if (publishedDays <= 0) {
    return {
      label: 'sous 1 à 2 semaines (estimation par défaut)',
      avgIntervalDays: null,
      isFallback: true,
    };
  }

  // Average gap between two publishing days within the window.
  const avg = WINDOW_DAYS / publishedDays;
  const rounded = Math.round(avg);

  if (avg <= 2) {
    return {
      label: 'sous quelques jours (médiateur très actif)',
      avgIntervalDays: rounded,
      isFallback: false,
    };
  }
  if (avg <= 7) {
    return {
      label: `sous environ ${rounded} jour${rounded > 1 ? 's' : ''}`,
      avgIntervalDays: rounded,
      isFallback: false,
    };
  }
  if (avg <= 14) {
    return {
      label: 'sous 1 à 2 semaines',
      avgIntervalDays: rounded,
      isFallback: false,
    };
  }
  return {
    label: 'sous plus de 2 semaines (publication peu fréquente)',
    avgIntervalDays: rounded,
    isFallback: false,
  };
};

export const useMediatorAvailabilityEstimate = (
  mediatorId: string | null | undefined,
  options: { enabled?: boolean } = {},
) => {
  const enabled = !!mediatorId && options.enabled !== false;

  return useQuery<AvailabilityEstimate>({
    queryKey: ['mediatorAvailabilityEstimate', mediatorId],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const since = format(subDays(new Date(), WINDOW_DAYS), 'yyyy-MM-dd');

      // We look at the `date` field (the day the mediator made themselves
      // available) rather than created_at, because that's the rhythm that
      // matters for "when will the next slot appear?".
      //
      // Note: RLS on availability_slots only exposes `is_booked = false`
      // rows to non-owner roles, so days that ended up fully booked are
      // invisible here. This biases the estimate slightly downward (we
      // under-count active mediators), which is acceptable for a soft
      // popover hint.
      const { data, error } = await supabase
        .from('availability_slots')
        .select('date')
        .eq('mediator_id', mediatorId as string)
        .gte('date', since);

      if (error) throw error;

      const distinctDates = new Set<string>((data ?? []).map(r => r.date));
      const publishedDays = distinctDates.size;
      const meta = buildLabel(publishedDays);

      return {
        publishedDays,
        avgIntervalDays: meta.avgIntervalDays,
        label: meta.label,
        isFallback: meta.isFallback,
      };
    },
  });
};

// Exposed for unit tests.
export const __test = { buildLabel, WINDOW_DAYS };
